// CAWT Welfare Trust - Central API Service for Hostinger Backend Server
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://trust.syntrobit.com/api';

// Helper to convert relative Hostinger upload paths to absolute URLs
export const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }

  const activeDomain = API_BASE_URL.replace(/\/api\/?$/, '');

  const sanitized = url.replace(/https?:\/\/cawt\.syntrobit\.com/gi, activeDomain);

  if (sanitized.startsWith('http://') || sanitized.startsWith('https://')) {
    return sanitized;
  }
  if (sanitized.startsWith('/uploads/')) {
    return `${activeDomain}${sanitized}`;
  }
  if (sanitized.startsWith('uploads/')) {
    return `${activeDomain}/${sanitized}`;
  }
  return sanitized;
};



// Resilient Fetch with timeout (prevents slow or hanging localhost loads)
const fetchWithTimeout = async (url, options = {}, timeoutMs = 2500) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      signal: options.signal || controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
};

// Initial Mock Seed Data for offline fallback
export const INITIAL_ADOPTIONS = [
  {
    id: 1,
    name: 'Sheru',
    age: '1 Year',
    gender: 'Male',
    breed: 'Indie Hound',
    description: 'Energetic, vaccinated rescue dog who loves outdoor play and pack companionship.',
    image_url: '/streetdog1.png',
    status: 'available',
  },
  {
    id: 2,
    name: 'Bella',
    age: '8 Months',
    gender: 'Female',
    breed: 'Indie Mix',
    description: 'Gentle, loving puppy rescued from highway traffic. Great with kids.',
    image_url: '/gallery/2.png',
    status: 'available',
  },
  {
    id: 3,
    name: 'Bruno',
    age: '1.5 Years',
    gender: 'Male',
    breed: 'Golden Indie',
    description: 'Affectionate rescue who loves belly rubs, long morning walks, and games.',
    image_url: '/gallery/1.png',
    status: 'available',
  },
  {
    id: 4,
    name: 'Milo',
    age: '6 Months',
    gender: 'Male',
    breed: 'Indie Puppy',
    description: 'Playful and curious puppy fully recovered from minor trauma. Dewormed & vaccinated.',
    image_url: '/puppy.png',
    status: 'available',
  },
  {
    id: 5,
    name: 'Luna',
    age: '2 Years',
    gender: 'Female',
    breed: 'Indie Shepherd',
    description: 'Loyal guardian dog with a calm, gentle soul. Spayed and vaccinated.',
    image_url: '/gallery/4.png',
    status: 'available',
  },
  {
    id: 6,
    name: 'Whiskers',
    age: '5 Months',
    gender: 'Female',
    breed: 'Domestic Shorthair',
    description: 'Rescued kitten, friendly and litter-trained. Loves cozy sunny spots.',
    image_url: '/catpeeking.png',
    status: 'available',
  },
  {
    id: 7,
    name: 'Tommy',
    age: '1 Year',
    gender: 'Male',
    breed: 'Indie Retriever Mix',
    description: 'Joyful and active street rescue. Great with other animals and highly obedient.',
    image_url: '/gallery/3.png',
    status: 'available',
  },
  {
    id: 8,
    name: 'Daisy',
    age: '7 Months',
    gender: 'Female',
    breed: 'Indie Mix',
    description: 'Sweet-tempered puppy rescued during anti-rabies drive. Gentle and eager to learn.',
    image_url: '/gallery/5.png',
    status: 'available',
  },
  {
    id: 9,
    name: 'Rocky',
    age: '2 Years',
    gender: 'Male',
    breed: 'Indie Terrier',
    description: 'Brave and intelligent rescue who recovered from fracture surgery at our clinic.',
    image_url: '/gallery/6.png',
    status: 'available',
  },
  {
    id: 10,
    name: 'Simba',
    age: '4 Months',
    gender: 'Male',
    breed: 'Tabby Kitten',
    description: 'Hand-reared orphan kitten, energetic, affectionate, and great with foster care.',
    image_url: '/kitten.png',
    status: 'available',
  },
  {
    id: 11,
    name: 'Coco',
    age: '1.2 Years',
    gender: 'Female',
    breed: 'Indie Hound',
    description: 'Gentle female dog with a shiny golden coat. Spayed, vaccinated, and house-trained.',
    image_url: '/gallery/7.png',
    status: 'available',
  },
  {
    id: 12,
    name: 'Leo',
    age: '1.5 Years',
    gender: 'Male',
    breed: 'Indie Mix',
    description: 'Sanctuary favorite who loves running in open fields and playing fetch with volunteers.',
    image_url: '/gallery/8.png',
    status: 'available',
  },
]


export const INITIAL_VOLUNTEERS = [
  {
    id: 1,
    name: 'Rahul Verma',
    email: 'rahul.verma@example.com',
    phone: '9876543210',
    area_of_interest: 'Adoption: Sheru',
    message: 'Address: Indiranagar, Bengaluru | Experience: yes | Household: apartment | Hours Alone: 4-6',
    status: 'rejected',
    created_at: new Date().toISOString(),
  },
  {
    id: 2,
    name: 'Priya Nair',
    email: 'priya.nair@example.com',
    phone: '9812345678',
    area_of_interest: 'Adoption: Bella',
    message: 'Address: Koramangala, Bengaluru | Experience: no | Household: independent_house | Hours Alone: 0-2',
    status: 'rejected',
    created_at: new Date().toISOString(),
  },
  {
    id: 3,
    name: 'Amit Patel',
    email: 'amit.patel@example.com',
    phone: '9900112233',
    area_of_interest: 'Adoption: Bruno',
    message: 'Address: HSR Layout, Bengaluru | Experience: yes | Household: apartment | Hours Alone: 2-4',
    status: 'pending',
    created_at: new Date().toISOString(),
  },
  {
    id: 4,
    name: 'Sneha Rao',
    email: 'sneha.rao@example.com',
    phone: '9744556677',
    area_of_interest: 'Feeding & Sanctuary Care',
    message: 'Interested in weekend feeding drives and sanctuary care for rescued animals.',
    status: 'approved',
    created_at: new Date().toISOString(),
  },
]

export const INITIAL_VOLUNTEER_PROFILES = [
  {
    id: 1,
    name: "Isaac & Linh",
    text: "The team at CAWT was very friendly and caring toward us and the animals during our shelter stay.",
    meta: "BY ISAAC AND LINH | JULY 2024",
    image_url: "/volunteer/v2.png"
  },
  {
    id: 2,
    name: "Sai Santhosh",
    text: "Hello all, I am very glad to share my rescue experience at CAWT during the past months.",
    meta: "BY SAI SANTHOSH | JUNE 2024",
    image_url: "/volunteer/v1.png"
  },
  {
    id: 3,
    name: "Georgia",
    text: "CAWT welcomed us with open arms despite our lack of experience, empowering us to care for street dogs.",
    meta: "BY GEORGIA | JUNE 2024",
    image_url: "/volunteer/v3.png"
  }
]

export const INITIAL_GALLERY = [
  { id: 1, title: 'Rescue Mission', image_url: '/gallery/1.png', row_number: 1 },
  { id: 2, title: 'Sanctuary Play', image_url: '/gallery/2.png', row_number: 1 },
  { id: 3, title: 'Medical Checkup', image_url: '/gallery/3.png', row_number: 2 },
  { id: 4, title: 'Vaccination Drive', image_url: '/gallery/4.png', row_number: 2 },
]

export const INITIAL_AWARENESS = [
  {
    id: 1,
    category: 'habits',
    badge: 'CANINE BEHAVIOR & HABITS',
    title: 'Understanding Dog Habits & Female Heat Cycles',
    description: 'Decoding natural canine signals, pack behavior, territorial habits, and female dog heat cycles and maternal protection instincts helps prevent misunderstandings and fosters harmony.',
    image_url: '/gallery/1.png',
    points: [
      { label: '1. Reading Body Language Signals', desc: 'A relaxed dog has soft eyes and a loose tail. Conversely, a stiff body, direct stare, raised hackles, or tucked tail indicates fear, stress, or high alertness. Never startle or corner a defensive dog.' },
      { label: '2. Female Heat Cycles & Maternal Protection', desc: 'Female street dogs enter heat 1 to 2 times a year. During heat or when nursing puppies, female dogs become fiercely protective of their nest. Avoid touching newborn litters or approaching mother dogs abruptly.' },
      { label: '3. Respecting Territorial Boundaries', desc: 'Dogs view specific streets or feeding spots as their home territory. Sudden fast movements, loud shouting, or running through their space can trigger defensive chase instincts.' }
    ]
  },
  {
    id: 2,
    category: 'bite_safety',
    badge: 'DOG BITE SAFETY & PROTOCOL',
    title: 'Dog Bite Prevention & Immediate First Aid Response',
    description: 'Knowing how to de-escalate canine encounters and taking immediate 15-minute emergency action following a bite saves lives and eliminates rabies risk.',
    image_url: '/gallery/2.png',
    points: [
      { label: '1. De-escalation (Stand Like a Tree)', desc: 'If approached by an agitated dog, never run, scream, or wave your arms. Stand still with hands by your side, avoid eye contact, and let the animal sniff you and lose interest before walking away slowly.' },
      { label: '2. Immediate 15-Minute Soap & Water Wash', desc: 'If bitten or scratched, immediately wash the wound vigorously under running tap water with soap for at least 15 full minutes. This mechanical washing destroys viral lipid envelopes.' },
      { label: '3. Post-Exposure Vaccination (ARV & RIG)', desc: 'Apply an antiseptic (like Povidone-Iodine) and seek urgent medical treatment. Administer the full Anti-Rabies Vaccine (ARV) schedule (Days 0, 3, 7, 14, 28) and Rabies Immunoglobulin (RIG) for category III wounds.' }
    ]
  },
  {
    id: 3,
    category: 'population_control',
    badge: 'HUMANE POPULATION MANAGEMENT',
    title: 'Animal Birth Control (ABC) & Sterilization Myths',
    description: 'Scientific Animal Birth Control (ABC) spay and neuter procedures are the only proven, ethical method to humanely stabilize stray dog populations.',
    image_url: '/gallery/3.png',
    points: [
      { label: '1. Spaying & Neutering Benefits', desc: 'Surgical sterilization eliminates reproductive cancers, stops endless litters, and significantly reduces hormone-driven pack aggression, night howling, and biting incidents.' },
      { label: '2. Catch-Neuter-Vaccinate-Release (CNVR)', desc: 'Street dogs are humanely caught, sterilized, vaccinated against rabies, given an ear-notch mark for identification, and released back into their original territory.' },
      { label: '3. Myths vs Scientific Facts', desc: 'Myth: Neutering makes dogs weak or lazy. Fact: Sterilization improves dog health, prevents life-threatening pyometra/testicular tumors, and extends life expectancy.' }
    ]
  },
  {
    id: 4,
    category: 'dog_health',
    badge: 'PET & COMMUNITY DOG HEALTH',
    title: 'Comprehensive Health Care for Healthy Dogs & Pets',
    description: 'Ensuring proper nutrition, routine core vaccinations, deworming, and parasite prevention maintains optimal health for both domestic pets and community dogs.',
    image_url: '/gallery/4.png',
    points: [
      { label: '1. Core Vaccination Schedules', desc: 'Pets and community dogs require annual anti-rabies boosters alongside DHPPi vaccines (protecting against Canine Distemper, Parvovirus, Hepatitis, Parainfluenza).' },
      { label: '2. Regular Deworming & Parasite Control', desc: 'Administer broad-spectrum dewormers every 3 to 6 months to prevent intestinal parasites. Use monthly topical or oral treatments for tick, flea, and heartworm prevention.' },
      { label: '3. Balanced Nutrition & Hydration', desc: 'Provide clean, fresh water daily and species-appropriate meals. Avoid toxic human foods such as cooked bones, chocolate, onions, garlic, grapes, and xylitol.' }
    ]
  },
  {
    id: 5,
    category: 'coexistence',
    badge: 'COEXISTENCE & LEGAL RIGHTS',
    title: 'Community Feeding, Laws & Peaceful Coexistence',
    description: 'Building harmonious neighborhood relations through organized feeding protocols and understanding legal animal welfare frameworks under Indian law.',
    image_url: '/streetdog1.png',
    points: [
      { label: '1. Designated Community Feeding Spots', desc: 'Feed street animals at designated times away from residential entryways, children play areas, and busy traffic roads to keep public spaces clean and peaceful.' },
      { label: '2. Legal Rights under ABC Rules 2023', desc: 'Under the Prevention of Cruelty to Animals Act (1960) and Animal Birth Control Rules (2023), feeding community dogs is a protected constitutional duty, and relocating stray dogs is strictly illegal.' },
      { label: '3. Neighbor & Colony Mediation', desc: 'Solve conflicts through constructive dialogue, establishing feeder responsibility, keeping dogs vaccinated, and engaging municipal bodies for ABC sterilization.' }
    ]
  }
]

export const INITIAL_WELFARE_ACTIVITIES = [
  {
    id: 1,
    title: '24/7 Street Dog & Trauma Emergency Rescues',
    category: 'rescue',
    badge: 'RAPID RESCUE UNIT',
    date: 'Active Daily',
    impact: '3,500+ Lives Saved',
    description: 'Equipped mobile ambulance response for severe vehicular accidents, deep well rescues, maggot-wound treatments, and emergency stabilization across urban and peri-urban zones.',
    image_url: '/gallery/1.png',
    images: [
      '/gallery/1.png',
      '/streetdog1.png',
      '/gallery/3.png',
      '/volunteer/v1.png'
    ],
    highlights: [
      { label: 'Emergency Ambulance Squad', desc: 'Fully fitted mobile unit with oxygen concentrator, stretchers, and critical trauma stabilization kits.' },
      { label: 'On-Spot Vet Triage', desc: 'Immediate pain-relief, antiseptic wound debridement, and splinting before shelter admission.' },
      { label: 'Surgical ICU Care', desc: 'Post-operative monitoring, wound dressings, and physical therapy in sterile hospital recovery wards.' },
      { label: 'Release & Pack Reintegration', desc: 'Once fully healed, rescued animals are safely returned to their home pack territory.' }
    ],
    status: 'active'
  },
  {
    id: 2,
    title: 'Mass Anti-Rabies & 9-in-1 Vaccination Camps',
    category: 'health_camp',
    badge: 'PREVENTATIVE HEALTHCARE',
    date: 'Quarterly Mega Camps',
    impact: '12,000+ Doses Administered',
    description: 'Systematic community-wide vaccination blitz targeting high-density community dog packs, preventing rabies transmission and boosting herd immunity in collaboration with local feeders.',
    image_url: '/gallery/4.png',
    images: [
      '/gallery/4.png',
      '/gallery/1.png',
      '/volunteer/v2.png',
      '/gallery/2.png'
    ],
    highlights: [
      { label: 'Geo-Tagged Records', desc: 'Every vaccinated dog is marked with non-toxic color spray and logged digitally with pack location.' },
      { label: 'Community Feeder Liaison', desc: 'Local community feeders coordinate dog gathering to minimize animal stress and ensure 100% coverage.' },
      { label: 'Booster Tracking System', desc: 'Automated digital registry to notify local caretakers when annual rabies booster cycles approach.' }
    ],
    status: 'active'
  },
  {
    id: 3,
    title: 'Large Animal & Stray Cattle Health Drives',
    category: 'health_camp',
    badge: 'RURAL & LARGE ANIMAL',
    date: 'Bi-Weekly Camps',
    impact: '800+ Cattle & Donkeys Treated',
    description: 'Dedicated health camps for injured stray cattle, working horses, and donkeys suffering from plastic ingestion, foot rot, cart trauma, and horn fractures.',
    image_url: '/gallery/3.png',
    images: [
      '/gallery/3.png',
      '/gallery/4.png',
      '/gallery/2.png'
    ],
    highlights: [
      { label: 'Specialized Cattle Treatment', desc: 'Mobile sling hoist, rumenotomy referrals, foot rot antiseptic treatments, and wound care.' },
      { label: 'Reflective Safety Collars', desc: 'Night-glow safety collars installed on free-roaming cattle to prevent highway vehicular collisions.' },
      { label: 'Rural Farmer Education', desc: 'Workshops for livestock owners on stall hygiene, parasite deworming, and non-cruelty handling.' }
    ],
    status: 'active'
  },
  {
    id: 4,
    title: 'Daily Community Animal Feeding & Nutrition Program',
    category: 'feeding',
    badge: 'DAILY HUNGER RELIEF',
    date: '365 Days a Year',
    impact: '1,200+ Meals Daily',
    description: 'Structured daily nutrition runs delivering boiled rice, broth, canine kibble, and fresh clean water across 28 feeding routes and industrial zones.',
    image_url: '/gallery/2.png',
    images: [
      '/gallery/2.png',
      '/streetdog1.png',
      '/volunteer/v3.png'
    ],
    highlights: [
      { label: 'Balanced Protein Diet', desc: 'Vet-approved hygienic meal preparation tailored for street pack stamina and coat health.' },
      { label: 'Clean Water Bowls', desc: 'Placement and daily refilling of cement water troughs across hot zones during peak summer.' },
      { label: 'Health Status Monitoring', desc: 'Feeders inspect dog packs daily to identify emerging tick infestations or early stage injuries.' }
    ],
    status: 'active'
  },
  {
    id: 5,
    title: 'Humane Animal Birth Control (ABC) & Sterilization',
    category: 'sterilization',
    badge: 'POPULATION MANAGEMENT',
    date: 'Year-Round Program',
    impact: '1,800+ Sterilizations',
    description: 'Ethical birth control surgeries performed by experienced veterinary surgeons following standard AWBI protocol to humanely manage stray dog populations and reduce territorial aggression.',
    image_url: '/volunteer/v1.png',
    images: [
      '/volunteer/v1.png',
      '/gallery/3.png',
      '/gallery/1.png'
    ],
    highlights: [
      { label: 'Post-Op Sanctuary Recovery', desc: '5-7 days supervised recovery with high-protein diet, thermal bedding, and antibiotic course.' },
      { label: 'Ear-Notching Identification', desc: 'Standard international V-notch marking to prevent repeat captures and verify sterilized status.' },
      { label: 'Zero Relocation Compliance', desc: 'Every sterilized dog is carefully released back to its specific original street territory.' }
    ],
    status: 'active'
  },
  {
    id: 6,
    title: 'School & Community Coexistence Awareness Workshops',
    category: 'awareness',
    badge: 'EDUCATION & OUTREACH',
    date: 'Monthly Sessions',
    impact: '45+ Schools & RWAs',
    description: 'Interactive humane education workshops with schools, apartment associations, and corporate offices explaining street dog body language, rabies safety protocols, and feeder guidelines.',
    image_url: '/volunteer/v3.png',
    images: [
      '/volunteer/v3.png',
      '/volunteer/v2.png',
      '/gallery/4.png'
    ],
    highlights: [
      { label: 'Bite Prevention Modules', desc: 'Practical demonstrations for children and security staff on how to behave when approached by dogs.' },
      { label: 'Feeder-RWA Mediation', desc: 'Designating harmonious feeding spots away from children play areas and building lobbies.' },
      { label: 'Legal Welfare Framework', desc: 'Educating resident welfare associations on AWBI guidelines and PCA Act 1960 animal protections.' }
    ],
    status: 'active'
  }
]

export const INITIAL_AWARENESS_REGISTRATIONS = [
  {
    id: 1,
    institution_name: 'Delhi Public School, South Campus',
    institution_type: 'School',
    contact_person: 'Dr. Priya Raman (Principal)',
    phone: '+91 98450 12345',
    email: 'priya.raman@dps.edu.in',
    city: 'Bengaluru South',
    expected_attendees: '250-500',
    preferred_date: 'Next Month (1st Week)',
    topics: '["Dog Bite Prevention & First Aid","Reading Animal Body Language","Humane Kindness to Strays"]',
    message: 'Requesting an interactive AV workshop and bite prevention session for grades 6 to 9.',
    status: 'pending',
    created_at: new Date(Date.now() - 3600000 * 24).toISOString(),
  },
  {
    id: 2,
    institution_name: 'Greenwood Heights Resident Welfare Association',
    institution_type: 'Residential Society / RWA',
    contact_person: 'Rajesh K. (Secretary)',
    phone: '+91 98801 98765',
    email: 'rwa.greenwood@gmail.com',
    city: 'Whitefield, Bengaluru',
    expected_attendees: '100-250',
    preferred_date: 'Upcoming Weekend',
    topics: '["Feeder-RWA Harmony & Feeding Spots","Rabies Safety & ARV Camps","Legal Rights under AWBI"]',
    message: 'We have 450 flats and want a community talk on peaceful dog feeding spots and rabies vaccination.',
    status: 'contacted',
    created_at: new Date(Date.now() - 3600000 * 48).toISOString(),
  },
]

export const getLocalData = (key, initial) => {
  try {
    const saved = localStorage.getItem(`cawt_${key}`)
    return saved ? JSON.parse(saved) : initial
  } catch (e) {
    return initial
  }
}

export const setLocalData = (key, data) => {
  try {
    localStorage.setItem(`cawt_${key}`, JSON.stringify(data))
  } catch (e) {
    console.error('LocalStorage write error:', e)
  }
}

const safeParseJson = async (res) => {
  if (!res || !res.ok) return null;
  const contentType = res.headers?.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
};

// -------------------------------------------------------------
// AUTHENTICATION
// -------------------------------------------------------------
export const loginAdmin = async (username, password) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/auth.php?action=login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }, 5000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      localStorage.setItem('cawt_admin_token', data.token)
      return { success: true, data }
    }
  } catch (e) {
    if (username === 'admin' && password === 'cawt@12345') {
      const demoToken = 'demo-local-token-' + Date.now()
      localStorage.setItem('cawt_admin_token', demoToken)
      return { success: true, message: 'Demo Admin Login Success' }
    }
  }
  return { success: false, message: 'Invalid admin credentials' }
}

export const checkAdminAuth = () => {
  return !!localStorage.getItem('cawt_admin_token')
}

export const logoutAdmin = () => {
  localStorage.removeItem('cawt_admin_token')
}

// -------------------------------------------------------------
// IMAGE UPLOAD API (Direct Hostinger Server Upload)
// -------------------------------------------------------------
export const uploadImage = async (file) => {
  if (!file) return null
  try {
    const formData = new FormData()
    formData.append('file', file)

    const res = await fetchWithTimeout(`${API_BASE_URL}/upload.php`, {
      method: 'POST',
      body: formData,
    }, 10000)
    const data = await safeParseJson(res)
    if (data && data.success && (data.fullUrl || data.url)) {
      return getFullImageUrl(data.fullUrl || data.url)
    }
  } catch (e) {
    console.error('Backend Image Upload Error:', e)
  }
  return null
}

// -------------------------------------------------------------
// ADOPTIONS API (Direct Hostinger MySQL Integration)
// -------------------------------------------------------------
export const fetchAdoptions = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoptions.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const serverItems = data.data.map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
      setLocalData('adoptions', serverItems)
      return serverItems
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Adoptions), using local fallback:', e)
  }
  return getLocalData('adoptions', INITIAL_ADOPTIONS).map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
}

export const createAdoption = async (petData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoptions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(petData),
    }, 6000)
    const data = await res.json()
    if (res.ok && data.success && data.data) {
      const serverPet = { ...data.data, image_url: getFullImageUrl(data.data.image_url) }
      const cached = getLocalData('adoptions', [])
      setLocalData('adoptions', [serverPet, ...cached])
      return serverPet
    }
  } catch (e) {
    console.error('Backend createAdoption Error:', e)
  }
  const current = getLocalData('adoptions', INITIAL_ADOPTIONS)
  const todayDate = new Date().toISOString().split('T')[0]
  const newPet = { ...petData, id: Date.now(), created_at: todayDate, date: todayDate, status: petData.status || 'available' }
  setLocalData('adoptions', [newPet, ...current])
  return newPet

}

export const updateAdoptionStatus = async (id, status) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/adoptions.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, status }),
    }, 6000)
  } catch (e) {
    console.error('Backend updateAdoptionStatus Error:', e)
  }
  const current = getLocalData('adoptions', INITIAL_ADOPTIONS)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, status } : item))
  setLocalData('adoptions', updated)
}

export const updateAdoption = async (id, petData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoptions.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...petData }),
    }, 6000)
    const data = await res.json()
    if (res.ok && data.success) {
      const updatedPet = { id, ...petData, image_url: getFullImageUrl(petData.image_url) }
      const cached = getLocalData('adoptions', [])
      setLocalData('adoptions', cached.map((item) => (String(item.id) === String(id) ? updatedPet : item)))
      return updatedPet
    }
  } catch (e) {
    console.error('Backend updateAdoption Error:', e)
  }
  const current = getLocalData('adoptions', INITIAL_ADOPTIONS)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...petData } : item))
  setLocalData('adoptions', updated)
  return { id, ...petData }
}

export const deleteAdoption = async (id) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoptions.php?id=${id}`, { method: 'DELETE' }, 6000)
    const data = await res.json()
    if (res.ok && data.success) {
      const cached = getLocalData('adoptions', [])
      setLocalData('adoptions', cached.filter((item) => String(item.id) !== String(id)))
      return true
    }
  } catch (e) {
    console.error('Backend deleteAdoption Error:', e)
  }
  const current = getLocalData('adoptions', INITIAL_ADOPTIONS)
  setLocalData('adoptions', current.filter((item) => String(item.id) !== String(id)))
  return false
}

// -------------------------------------------------------------
// VOLUNTEERS API
// -------------------------------------------------------------
export const fetchVolunteers = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/volunteers.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      setLocalData('volunteers', data.data)
      return data.data
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API, using local fallback:', e)
  }
  return getLocalData('volunteers', INITIAL_VOLUNTEERS)
}

export const submitVolunteer = async (vData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/volunteers.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      return data
    }
  } catch (e) {
    console.error('Backend submitVolunteer Error:', e)
  }
  const current = getLocalData('volunteers', INITIAL_VOLUNTEERS)
  const newSubmission = { ...vData, id: Date.now(), status: 'pending', created_at: new Date().toISOString() }
  setLocalData('volunteers', [newSubmission, ...current])
  return { success: true }
}

export const updateVolunteerStatus = async (id, status) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/volunteers.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, status }),
    }, 6000)
  } catch (e) {
    console.error('Backend updateVolunteerStatus Error:', e)
  }
  const current = getLocalData('volunteers', INITIAL_VOLUNTEERS)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, status } : item))
  setLocalData('volunteers', updated)
}

export const deleteVolunteer = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/volunteers.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteVolunteer Error:', e)
  }
  const current = getLocalData('volunteers', INITIAL_VOLUNTEERS)
  const updated = current.filter((item) => String(item.id) !== String(id))
  setLocalData('volunteers', updated)
}

export const fetchVolunteerProfiles = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/volunteer_profiles.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const serverItems = data.data.map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
      setLocalData('volunteer_profiles', serverItems)
      return serverItems
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Volunteer Profiles), using local fallback:', e)
  }
  const rawData = getLocalData('volunteer_profiles', null)
  const items = rawData !== null ? rawData : INITIAL_VOLUNTEER_PROFILES
  const uniqueData = Array.from(new Map(items.map((item) => [item.name || item.id, item])).values())
  return uniqueData.map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
}


export const createVolunteerProfile = async (profileData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/volunteer_profiles.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profileData),
    }, 6000)
    const data = await res.json()
    if (res.ok && data.success && data.data) {
      const serverProfile = { ...data.data, image_url: getFullImageUrl(data.data.image_url) }
      const cached = getLocalData('volunteer_profiles', [])
      setLocalData('volunteer_profiles', [serverProfile, ...cached])
      return serverProfile
    }
  } catch (e) {
    console.error('Backend createVolunteerProfile Error:', e)
  }
  const current = getLocalData('volunteer_profiles', INITIAL_VOLUNTEER_PROFILES)
  const newProfile = { ...profileData, id: Date.now() }
  setLocalData('volunteer_profiles', [newProfile, ...current])
  return { ...newProfile, image_url: getFullImageUrl(newProfile.image_url) }
}

export const updateVolunteerProfile = async (id, profileData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/volunteer_profiles.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...profileData }),
    }, 6000)
    const data = await res.json()
    if (res.ok && data.success) {
      const updated = { id, ...profileData, image_url: getFullImageUrl(profileData.image_url) }
      const cached = getLocalData('volunteer_profiles', [])
      setLocalData('volunteer_profiles', cached.map((item) => (String(item.id) === String(id) ? updated : item)))
      return updated
    }
  } catch (e) {
    console.error('Backend updateVolunteerProfile Error:', e)
  }
  const current = getLocalData('volunteer_profiles', INITIAL_VOLUNTEER_PROFILES)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...profileData } : item))
  setLocalData('volunteer_profiles', updated)
  return { id, ...profileData }
}

export const deleteVolunteerProfile = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/volunteer_profiles.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteVolunteerProfile Error:', e)
  }
  const current = getLocalData('volunteer_profiles', INITIAL_VOLUNTEER_PROFILES)
  setLocalData('volunteer_profiles', current.filter((item) => String(item.id) !== String(id)))
}

// -------------------------------------------------------------
// GALLERY API
// -------------------------------------------------------------
export const fetchGallery = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/gallery.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const serverItems = data.data.map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
      setLocalData('gallery', serverItems)
      return serverItems
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Gallery), using local fallback:', e)
  }
  return getLocalData('gallery', INITIAL_GALLERY).map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
}

export const createGalleryPhoto = async (photoData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/gallery.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(photoData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success && data.data) {
      const serverPhoto = { ...data.data, image_url: getFullImageUrl(data.data.image_url) }
      const cached = getLocalData('gallery', [])
      setLocalData('gallery', [serverPhoto, ...cached])
      return serverPhoto
    }
  } catch (e) {
    console.error('Backend createGalleryPhoto Error:', e)
  }
  const current = getLocalData('gallery', INITIAL_GALLERY)
  const newPhoto = { ...photoData, id: Date.now() }
  setLocalData('gallery', [newPhoto, ...current])
  return { ...newPhoto, image_url: getFullImageUrl(newPhoto.image_url) }
}

export const updateGalleryPhoto = async (id, photoData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/gallery.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...photoData }),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const updated = { id, ...photoData, image_url: getFullImageUrl(photoData.image_url) }
      const cached = getLocalData('gallery', [])
      setLocalData('gallery', cached.map((item) => (String(item.id) === String(id) ? updated : item)))
      return updated
    }
  } catch (e) {
    console.error('Backend updateGalleryPhoto Error:', e)
  }
  const current = getLocalData('gallery', INITIAL_GALLERY)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...photoData } : item))
  setLocalData('gallery', updated)
  return { id, ...photoData }
}

export const deleteGalleryPhoto = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/gallery.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteGalleryPhoto Error:', e)
  }
  const current = getLocalData('gallery', INITIAL_GALLERY)
  setLocalData('gallery', current.filter((item) => String(item.id) !== String(id)))
}

// -------------------------------------------------------------
// AWARENESS & COEXISTENCE GUIDES API
// -------------------------------------------------------------
export const fetchAwarenessArticles = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/awareness.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const serverItems = data.data.map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
      setLocalData('awareness', serverItems)
      return serverItems
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Awareness), using local fallback:', e)
  }
  return getLocalData('awareness', INITIAL_AWARENESS).map((item) => ({ ...item, image_url: getFullImageUrl(item.image_url) }))
}

export const createAwarenessArticle = async (articleData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/awareness.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(articleData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success && data.data) {
      const serverArticle = { ...data.data, image_url: getFullImageUrl(data.data.image_url) }
      const cached = getLocalData('awareness', [])
      setLocalData('awareness', [serverArticle, ...cached])
      return serverArticle
    }
  } catch (e) {
    console.error('Backend createAwarenessArticle Error:', e)
  }
  const current = getLocalData('awareness', INITIAL_AWARENESS)
  const newArticle = { ...articleData, id: Date.now() }
  setLocalData('awareness', [newArticle, ...current])
  return { ...newArticle, image_url: getFullImageUrl(newArticle.image_url) }
}

export const updateAwarenessArticle = async (id, articleData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/awareness.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...articleData }),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const updated = { id, ...articleData, image_url: getFullImageUrl(articleData.image_url) }
      const cached = getLocalData('awareness', [])
      setLocalData('awareness', cached.map((item) => (String(item.id) === String(id) ? updated : item)))
      return updated
    }
  } catch (e) {
    console.error('Backend updateAwarenessArticle Error:', e)
  }
  const current = getLocalData('awareness', INITIAL_AWARENESS)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...articleData } : item))
  setLocalData('awareness', updated)
  return { id, ...articleData }
}

export const deleteAwarenessArticle = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/awareness.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteAwarenessArticle Error:', e)
  }
  const current = getLocalData('awareness', INITIAL_AWARENESS)
  setLocalData('awareness', current.filter((item) => String(item.id) !== String(id)))
}

// ----------------------------------------------------
// 6. WELFARE ACTIVITIES (Rescue, Camps, Feeding, Sterilization, Community)
// ----------------------------------------------------
export const fetchWelfareActivities = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/welfare_activities.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const serverItems = data.data.map((item) => ({
        ...item,
        image_url: getFullImageUrl(item.image_url),
        images: Array.isArray(item.images) && item.images.length > 0
          ? item.images.map(getFullImageUrl)
          : [getFullImageUrl(item.image_url)],
      }))
      setLocalData('welfare_activities', serverItems)
      return serverItems
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Welfare Activities), using local fallback:', e)
  }
  return getLocalData('welfare_activities', INITIAL_WELFARE_ACTIVITIES).map((item) => ({
    ...item,
    image_url: getFullImageUrl(item.image_url),
    images: Array.isArray(item.images) && item.images.length > 0
      ? item.images.map(getFullImageUrl)
      : [getFullImageUrl(item.image_url)],
  }))
}

export const createWelfareActivity = async (activityData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/welfare_activities.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(activityData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success && data.data) {
      const serverItem = {
        ...data.data,
        image_url: getFullImageUrl(data.data.image_url),
        images: Array.isArray(data.data.images) ? data.data.images.map(getFullImageUrl) : [getFullImageUrl(data.data.image_url)],
      }
      const cached = getLocalData('welfare_activities', [])
      setLocalData('welfare_activities', [serverItem, ...cached])
      return serverItem
    }
  } catch (e) {
    console.error('Backend createWelfareActivity Error:', e)
  }
  const current = getLocalData('welfare_activities', INITIAL_WELFARE_ACTIVITIES)
  const newActivity = {
    ...activityData,
    id: Date.now(),
    image_url: getFullImageUrl(activityData.image_url),
    images: Array.isArray(activityData.images) && activityData.images.length > 0
      ? activityData.images.map(getFullImageUrl)
      : [getFullImageUrl(activityData.image_url)],
  }
  setLocalData('welfare_activities', [newActivity, ...current])
  return newActivity
}

export const updateWelfareActivity = async (id, activityData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/welfare_activities.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...activityData }),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const updated = {
        id,
        ...activityData,
        image_url: getFullImageUrl(activityData.image_url),
        images: Array.isArray(activityData.images) ? activityData.images.map(getFullImageUrl) : [getFullImageUrl(activityData.image_url)],
      }
      const cached = getLocalData('welfare_activities', [])
      setLocalData('welfare_activities', cached.map((item) => (String(item.id) === String(id) ? updated : item)))
      return updated
    }
  } catch (e) {
    console.error('Backend updateWelfareActivity Error:', e)
  }
  const current = getLocalData('welfare_activities', INITIAL_WELFARE_ACTIVITIES)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...activityData } : item))
  setLocalData('welfare_activities', updated)
  return { id, ...activityData }
}

export const deleteWelfareActivity = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/welfare_activities.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteWelfareActivity Error:', e)
  }
  const current = getLocalData('welfare_activities', INITIAL_WELFARE_ACTIVITIES)
  setLocalData('welfare_activities', current.filter((item) => String(item.id) !== String(id)))
}

export const DEFAULT_DONATION_SETTINGS = {
  qr_image_url: '',
  upi_id: 'cawt.trust@ybl',
  upi_holder_name: 'Companion Animal Welfare Trust',
  account_name: 'Companion Animal Welfare Trust',
  bank_name: 'State Bank of India',
  account_number: '98765432101',
  ifsc_code: 'SBIN0001234',
  branch_name: 'Bengaluru Main Branch',
  tax_note: '© CAWT trust is 80G Tax Exempt',
  subtext: 'Scan with GPay, PhonePe, Paytm, or BHIM',
}

export const fetchDonationSettings = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/donation_settings.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && data.data) {
      const resolved = {
        ...data.data,
        qr_image_url: getFullImageUrl(data.data.qr_image_url || ''),
      }
      setLocalData('donation_settings', resolved)
      return resolved
    }
  } catch (e) {
    console.error('Backend fetchDonationSettings Error:', e)
  }
  return getLocalData('donation_settings', DEFAULT_DONATION_SETTINGS)
}

export const updateDonationSettings = async (settingsData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/donation_settings.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settingsData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const resolved = {
        ...settingsData,
        qr_image_url: getFullImageUrl(settingsData.qr_image_url || ''),
      }
      setLocalData('donation_settings', resolved)
      return { success: true, data: resolved }
    }
  } catch (e) {
    console.error('Backend updateDonationSettings Error:', e)
  }
  const resolved = {
    ...settingsData,
    qr_image_url: getFullImageUrl(settingsData.qr_image_url || ''),
  }
  setLocalData('donation_settings', resolved)
  return { success: true, data: resolved }
}

// -------------------------------------------------------------
// ADOPTION STORIES (HAPPY ADOPTION STORIES)
// -------------------------------------------------------------
export const INITIAL_ADOPTION_STORIES = [
  {
    id: 1,
    quote: "Adopting Bruno through Chetak Animal Welfare Trust was the best decision of our lives. He brought immense warmth, energy, and happiness into our home.",
    pet_name: "Bruno",
    adopter_name: "The Sharma Family",
    date: "JULY 2024",
    image_url: "/adopters/3.png",
  },
  {
    id: 2,
    quote: "Chetak Animal Welfare Trust rescued Rio during a severe storm when he had a critical eye injury. Thanks to their extraordinary care and treatment, he healed beautifully and now rules our heart and living room!",
    pet_name: "Rio",
    adopter_name: "Priya & Rahul",
    date: "JUNE 2024",
    image_url: "/adopters/1.png",
  },
  {
    id: 3,
    quote: "Sheru had a severe leg fracture when Chetak Animal Welfare Trust rescued him. Witnessing his complete recovery and adopting him is deeply fulfilling.",
    pet_name: "Sheru",
    adopter_name: "Karthik & Ananya",
    date: "MAY 2024",
    image_url: "/adopters/5.png",
  },
  {
    id: 4,
    quote: "Bella was timid and scared when we first met her. Thanks to Chetak Animal Welfare Trust's foster training, she is now the happiest pup in our society!",
    pet_name: "Bella",
    adopter_name: "Sneha M.",
    date: "APRIL 2024",
    image_url: "/adopters/2.png",
  },
  {
    id: 5,
    quote: "Oliver found us during an adoption drive at Whitefield. He brings unconditional love and endless purrs every single day.",
    pet_name: "Oliver",
    adopter_name: "Vikram & Diya",
    date: "MARCH 2024",
    image_url: "/adopters/4.png",
  },
]

export const fetchAdoptionStories = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoption_stories.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      const resolved = data.data.map((item) => ({
        ...item,
        image_url: getFullImageUrl(item.image_url || item.image),
        image: getFullImageUrl(item.image_url || item.image),
      }))
      setLocalData('adoption_stories', resolved)
      return resolved
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Adoption Stories), using local fallback:', e)
  }
  const cached = getLocalData('adoption_stories', INITIAL_ADOPTION_STORIES)
  return cached.map((item) => ({
    ...item,
    image_url: getFullImageUrl(item.image_url || item.image),
    image: getFullImageUrl(item.image_url || item.image),
  }))
}

export const createAdoptionStory = async (storyData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoption_stories.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(storyData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success && data.data) {
      const created = {
        ...data.data,
        image_url: getFullImageUrl(data.data.image_url),
        image: getFullImageUrl(data.data.image_url),
      }
      const cached = getLocalData('adoption_stories', [])
      setLocalData('adoption_stories', [...cached, created])
      return created
    }
  } catch (e) {
    console.error('Backend createAdoptionStory Error:', e)
  }
  const current = getLocalData('adoption_stories', INITIAL_ADOPTION_STORIES)
  const newStory = {
    id: Date.now(),
    ...storyData,
    image_url: getFullImageUrl(storyData.image_url),
    image: getFullImageUrl(storyData.image_url),
  }
  setLocalData('adoption_stories', [...current, newStory])
  return newStory
}

export const updateAdoptionStory = async (id, storyData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/adoption_stories.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, ...storyData }),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const updated = {
        id,
        ...storyData,
        image_url: getFullImageUrl(storyData.image_url),
        image: getFullImageUrl(storyData.image_url),
      }
      const cached = getLocalData('adoption_stories', [])
      setLocalData('adoption_stories', cached.map((item) => (String(item.id) === String(id) ? updated : item)))
      return updated
    }
  } catch (e) {
    console.error('Backend updateAdoptionStory Error:', e)
  }
  const current = getLocalData('adoption_stories', INITIAL_ADOPTION_STORIES)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, ...storyData } : item))
  setLocalData('adoption_stories', updated)
  return { id, ...storyData }
}

export const deleteAdoptionStory = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/adoption_stories.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteAdoptionStory Error:', e)
  }
  const current = getLocalData('adoption_stories', INITIAL_ADOPTION_STORIES)
  setLocalData('adoption_stories', current.filter((item) => String(item.id) !== String(id)))
}

// -------------------------------------------------------------
// AWARENESS PROGRAM REGISTRATIONS API
// -------------------------------------------------------------
export const fetchAwarenessRegistrations = async () => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/awareness_registrations.php`, {}, 2500)
    const data = await safeParseJson(res)
    if (data && data.success && Array.isArray(data.data)) {
      setLocalData('awareness_registrations', data.data)
      return data.data
    }
  } catch (e) {
    console.warn('Unable to connect to Hostinger API (Awareness Registrations), using local fallback:', e)
  }
  return getLocalData('awareness_registrations', INITIAL_AWARENESS_REGISTRATIONS)
}

export const submitAwarenessRegistration = async (formData) => {
  try {
    const res = await fetchWithTimeout(`${API_BASE_URL}/awareness_registrations.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    }, 6000)
    const data = await safeParseJson(res)
    if (data && data.success) {
      const current = getLocalData('awareness_registrations', INITIAL_AWARENESS_REGISTRATIONS)
      const newEntry = {
        ...formData,
        id: data.id || Date.now(),
        status: 'pending',
        topics: typeof formData.topics === 'object' ? JSON.stringify(formData.topics) : formData.topics,
        created_at: new Date().toISOString(),
      }
      setLocalData('awareness_registrations', [newEntry, ...current])
      return data
    }
  } catch (e) {
    console.error('Backend submitAwarenessRegistration Error:', e)
  }
  const current = getLocalData('awareness_registrations', INITIAL_AWARENESS_REGISTRATIONS)
  const newEntry = {
    ...formData,
    id: Date.now(),
    status: 'pending',
    topics: typeof formData.topics === 'object' ? JSON.stringify(formData.topics) : formData.topics,
    created_at: new Date().toISOString(),
  }
  setLocalData('awareness_registrations', [newEntry, ...current])
  return { success: true, message: 'Awareness program registration received successfully' }
}

export const updateAwarenessRegistrationStatus = async (id, status) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/awareness_registrations.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, status }),
    }, 6000)
  } catch (e) {
    console.error('Backend updateAwarenessRegistrationStatus Error:', e)
  }
  const current = getLocalData('awareness_registrations', INITIAL_AWARENESS_REGISTRATIONS)
  const updated = current.map((item) => (String(item.id) === String(id) ? { ...item, status } : item))
  setLocalData('awareness_registrations', updated)
}

export const deleteAwarenessRegistration = async (id) => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/awareness_registrations.php?id=${id}`, { method: 'DELETE' }, 6000)
  } catch (e) {
    console.error('Backend deleteAwarenessRegistration Error:', e)
  }
  const current = getLocalData('awareness_registrations', INITIAL_AWARENESS_REGISTRATIONS)
  const updated = current.filter((item) => String(item.id) !== String(id))
  setLocalData('awareness_registrations', updated)
}
